import { useState, FormEvent, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSharedState } from "state";
import ModalComponent from "components/modal";
import { addLevel, addSublevel, updateLevel } from "api/level";
import { IconCheck, IconMinus, IconRestore, IconX } from "@tabler/icons-react";
import DangerModal from "components/modal/danger";
import { LevelDefault, LevelType } from "types/dict";
import Select from "react-select";

type FormErrors = Record<string, boolean>;

type FormRecords = {
  budget_type: string;
  forecast_min_level: number | null;
};

type SelectOption = {
  value: number;
  label: string;
};

const REQUIRED_FIELDS = ["budget_type"] as const;

export default function NewLevel() {
  const { levels, getLevelsData, notify } = useSharedState();
  const { levelId } = useParams();
  const navigate = useNavigate();

  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [changedLevels, setChangedLevels] = useState<Set<number>>(new Set());
  const [initialLevels, setInitialLevels] = useState<LevelType[]>([]);

  const [formRecords, setFormRecords] = useState<FormRecords>({ budget_type: "", forecast_min_level: null });
  const [errors, setErrors] = useState<FormErrors>({});

  const [columns, setColumns] = useState<number[]>([0]);
  const [rows, setRows] = useState<LevelType[]>([LevelDefault]);

  const [levelToDelete, setLevelToDelete] = useState<LevelType>(LevelDefault);

  const [isFormLoading, setFormLoading] = useState<boolean>(false);
  const [isFormSubmitted, setFormSubmitted] = useState<boolean>(false);

  const minLevelOptions = useMemo(() => {
    return columns
      .sort((a, b) => a - b)
      .map((c) => ({
        label: `Уровень ${c + 1}`,
        value: c + 1,
      }));
  }, [columns]);

  const deactivateLevelDanger = useMemo(() => {
    return `Вы действительно хотите удалить уровень "${levelToDelete.budget_type}"?`;
  }, [levelToDelete]);

  const levelItem = useMemo(() => {
    return levels.find((t) => t.budget_type_id === Number(levelId));
  }, [levels, levelId]);

  useEffect(() => {
    if (levelId) {
      if (levelItem) {
        setFormRecords({
          budget_type: levelItem.budget_type!,
          forecast_min_level: levelItem.forecast_min_level!,
        });

        const levelRows = levels
          .filter((lvl) => lvl.parent_budget_type_id === levelItem.budget_type_id)
          .sort((a, b) => a.level_number! - b.level_number!)
          .map((r) => ({
            ...r,
            level_number: r.level_number! - 1,
          }));
        setRows(levelRows);

        const levelColumns = levelRows
          .map((r) => r.level_number)
          .filter((value, index, array) => array.indexOf(value) === index)
          .sort((a, b) => a - b);

        console.log(levelColumns);

        setColumns(levelColumns);
      } else {
        notify("Уровень не найден", "danger");
        navigate("/levels");
      }
    }
  }, [levelId, levelItem]);

  const openLevelModal = () => {
    const button = document.createElement("button");
    button.setAttribute("data-bs-toggle", "modal");
    button.setAttribute("data-bs-target", "#modal");
    document.body.appendChild(button);
    button.click();
    button.remove();
  };

  const levelFields = [
    {
      id: "budget_type",
      title: "Название бюджета",
      type: "text",
      placeholder: "Введите название",
      options: undefined,
    },
    {
      id: "forecast_min_level",
      title: "Нижний уровень прогноза",
      type: "select",
      placeholder: "Выберите уровень",
      options: minLevelOptions,
    },
  ] as const;

  const validateForm = () => {
    const newErrors: FormErrors = {};
    REQUIRED_FIELDS.forEach((field) => {
      const value = formRecords[field];
      if (Array.isArray(value) ? value.length === 0 : !value?.toString().trim()) {
        newErrors[field] = true;
      }
    });

    // Validate all rows' budget_type
    rows.forEach((row, idx) => {
      if (!row.budget_type || !row.budget_type.trim()) {
        newErrors[`row_${idx}`] = true;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  type InputChangeEvent = React.ChangeEvent<HTMLInputElement>;
  type SingleSelectEvent = SelectOption | null;

  const handleInputChange = (e: InputChangeEvent | SingleSelectEvent, fieldId?: string) => {
    // const { name, value } = e.target;
    // setFormRecords((prev) => ({ ...prev, [name]: value }));
    // setErrors((prev) => ({ ...prev, [name]: false }));

    if (e && "target" in e) {
      // Handle input change
      const { name, value } = e.target;
      setFormRecords((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: false }));
    } else {
      // Handle select change
      // @ts-ignore
      const values = Array.isArray(e) ? e.map((option) => option.value) : e ? e.value : "";
      setFormRecords((prev) => ({ ...prev, [fieldId!]: values }));
      setErrors((prev) => ({ ...prev, [fieldId!]: false }));
    }
  };

  const cancelEdit = () => {
    setIsEditMode(false);
    setChangedLevels(new Set());
    setRows((prev) => [...prev.filter((row) => row.budget_type_id)]);
  };

  const submitLevel = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      notify("Заполните все поля!", "danger");
      return;
    }
    setFormLoading(true);

    const incrementedRows = [
      ...rows.map((r) => ({
        ...r,
        budget_type: r.budget_type!.trim(),
        level_number: r.level_number! + 1,
      })),
    ];

    try {
      if (!levelId) {
        await addLevel(formRecords.budget_type, columns.length, formRecords.forecast_min_level!, incrementedRows);
        notify("Уровень создан", "success");
      }

      setFormSubmitted(true);
      getLevelsData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", levelId ? "Ошибка при обновлении уровня" : "Ошибка при создании уровня");
    } finally {
      setFormLoading(false);
    }
  };

  const handleLevelDelete = async () => {
    openLevelModal();
    setFormLoading(true);

    try {
      await updateLevel(levelToDelete, false);
      notify("Уровень удален", "success");

      setLevelToDelete(LevelDefault);
      getLevelsData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при удалении уровня");
    } finally {
      setFormLoading(false);
    }
  };

  const handleAddSublevel = async (lvl: LevelType) => {
    if (!lvl.budget_type) {
      notify("Введите название", "danger", "Ошибка при добавлении уровня");
      return;
    }

    try {
      setFormLoading(true);
      await addSublevel({ ...lvl, level_number: lvl.level_number! + 1 }, levelItem?.budget_type_id!);
      notify("Уровень добавлен", "success");

      setLevelToDelete(LevelDefault);
      getLevelsData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при добавлении уровня");
    } finally {
      setFormLoading(false);
    }
  };

  const handleRowInputChange = (rowIndex: number, value: string) => {
    setRows((prev) => prev.map((row, index) => (index === rowIndex ? { ...row, budget_type: value } : row)));
    setErrors((prev) => ({ ...prev, [`row_${rowIndex}`]: false }));
  };

  const addRowInput = (rowIndex: number) => {
    const currentRow = rows[rowIndex];
    setRows((prev) => [...prev, { ...LevelDefault, level_number: currentRow.level_number, budget_type: "" }]);
  };

  const deleteRowInput = (rowIndex: number) => {
    const row = rows[rowIndex];
    // Find all rows in the same column
    const rowsInCol = rows.filter((r) => r.level_number === row.level_number);
    if (rowsInCol.length === 1) return; // Prevent deleting last row in column
    // Remove the row
    const filteredRows = rows.filter((_, idx) => idx !== rowIndex);
    setRows(filteredRows);
  };

  const addColumn = () => {
    setColumns((prev) => [...prev, prev.length]);
    setRows((prev) => [...prev, { ...LevelDefault, level_number: columns.length, budget_type: "" }]);
  };

  // Helper to reindex columns and rows after deletion
  const reindexColumnsAndRows = (columns: number[], rows: LevelType[]) => {
    // Sort columns and assign new indices
    const newColumns = columns.map((_, idx) => idx);
    // Map old column index to new
    const colMap = new Map<number, number>();
    columns.forEach((col, idx) => colMap.set(col, idx));
    // Update rows' level_number
    const newRows = rows.map((row) => ({
      ...row,
      level_number: colMap.get(row.level_number!) ?? row.level_number,
    }));
    return { newColumns, newRows };
  };

  const deleteColumn = (col: number) => {
    if (columns.length === 1) return; // Prevent deleting last column
    // Remove the column and all rows in it
    const filteredColumns = columns.filter((c) => c !== col);
    const filteredRows = rows.filter((r) => r.level_number !== col);
    // Reindex columns and rows
    const { newColumns, newRows } = reindexColumnsAndRows(filteredColumns, filteredRows);
    setColumns(newColumns);
    setRows(newRows);
  };

  const renderField = ({ id, title, type, placeholder, options }: (typeof levelFields)[number]) => {
    return (
      <div className="col-lg-3" key={id}>
        <label className={`form-label ${type === "text" && "required"}`}>{title}</label>
        <div className="input-icon">
          {type === "select" ? (
            <Select
              name={id}
              placeholder={placeholder}
              value={options?.find((opt) => opt.value === formRecords[id as keyof FormRecords])}
              onChange={(newValue) => handleInputChange(newValue, id)}
              isDisabled={isFormLoading || !!levelId}
              options={options}
              isSearchable
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          ) : (
            <input
              type="text"
              className={`form-control ${errors[id] ? "is-invalid" : ""}`}
              name={id}
              placeholder={placeholder}
              value={(formRecords[id as keyof FormRecords] as string) || ""}
              onChange={handleInputChange}
              disabled={isFormLoading || !!levelId}
            />
          )}

          <div className="invalid-feedback">Введите корректное значение</div>
          {isFormLoading && (
            <span className="input-icon-addon">
              <div className="spinner-border spinner-border-sm text-secondary" role="status" />
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderRows = () => (
    <div className="row">
      <div className="col-12">
        {columns.map((col) => (
          <div key={col} className="d-flex flex-wrap gap-3 mb-3">
            <div style={{ position: "relative" }}>
              <span className="d-flex items-center badge bg-primary" style={{ height: 36, width: 36 }}>
                {col + 1}
              </span>
              {!levelId && columns.length > 1 && (
                <button
                  type="button"
                  className="badge badge-notification badge-pill bg-secondary cursor-pointer"
                  style={{ padding: 2 }}
                  title="Удалить уровень"
                  onClick={() => deleteColumn(col)}
                  disabled={isFormLoading}
                >
                  <IconMinus size={16} />
                </button>
              )}
            </div>

            {rows
              .filter((row) => row.level_number === col)
              .map((row, index) => (
                <div key={`${col}-${index}`} className="d-flex gap-2">
                  <div className="input-icon">
                    <input
                      type="text"
                      className={`form-control ${errors[`row_${rows.indexOf(row)}`] ? "is-invalid" : ""} ${
                        changedLevels.has(row.budget_type_id!) && !row.status
                          ? "border-success"
                          : (row.budget_type_id && !row.status) ||
                            (changedLevels.has(row.budget_type_id!) && row.status)
                          ? "border-danger"
                          : ""
                      }`}
                      placeholder="Введите тип бюджета"
                      title={row.budget_type!}
                      value={row.budget_type!}
                      onChange={(e) => handleRowInputChange(rows.indexOf(row), e.target.value)}
                      disabled={isFormLoading || !!row.budget_type_id}
                    />
                    {!levelId && rows.filter((row) => row.level_number === col).length > 1 && (
                      <button
                        type="button"
                        className="badge badge-notification badge-pill bg-secondary cursor-pointer"
                        style={{ padding: 2 }}
                        title="Удалить"
                        onClick={() => deleteRowInput(rows.indexOf(row))}
                        disabled={isFormLoading}
                      >
                        <IconMinus size={16} />
                      </button>
                    )}
                    {isEditMode && row.budget_type_id && (
                      <button
                        type="button"
                        className={`badge badge-notification badge-pill cursor-pointer ${
                          changedLevels.has(row.budget_type_id!)
                            ? row.status
                              ? "bg-danger-lt"
                              : "bg-success-lt"
                            : row.status
                            ? "bg-danger"
                            : "bg-success"
                        }`}
                        style={{ padding: 2 }}
                        title={row.status ? "Деактивировать" : "Активировать"}
                        onClick={() => handleLevelStatusToggle(row)}
                        disabled={isFormLoading}
                      >
                        {row.status ? <IconX size={16} /> : <IconRestore size={16} />}
                      </button>
                    )}
                  </div>
                </div>
              ))}

            {(!levelId || isEditMode) && (
              <button
                type="button"
                className="btn btn-outline-primary"
                title="Добавить уровень"
                onClick={() => addRowInput(rows.findIndex((r) => r.level_number === col))}
                disabled={isFormLoading}
              >
                +
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderColumns = () => (
    <div className="row mb-3">
      <div className="col-12">
        <button type="button" className="btn btn-outline-primary" onClick={addColumn} disabled={isFormLoading}>
          Добавить уровень
        </button>
      </div>
    </div>
  );

  const newLevelBody = (
    <form id="levelForm" className="row" onSubmit={submitLevel}>
      <div className="row mb-3">{levelFields.map(renderField)}</div>

      {renderRows()}

      {!levelId && renderColumns()}
    </form>
  );
  const renderFooter = () => {
    if (!levelId) {
      return (
        <button type="submit" form="levelForm" className="btn btn-success d-flex gap-1" disabled={isFormLoading}>
          Создать уровень
          {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
        </button>
      );
    }

    if (!isEditMode) {
      return (
        <button type="button" className="btn btn-primary" onClick={() => setIsEditMode(true)}>
          Редактировать
        </button>
      );
    }

    return (
      <div className="d-flex gap-2">
        <button type="button" className="btn btn-ghost-primary" onClick={cancelEdit}>
          Отменить
        </button>
        <button
          type="button"
          className="btn btn-success d-flex gap-1"
          onClick={handleSaveChanges}
          disabled={isFormLoading || !hasChanges()}
        >
          Сохранить
          {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
        </button>
      </div>
    );
  };

  const handleLevelStatusToggle = (row: LevelType) => {
    if (!row.budget_type_id) return;
    setChangedLevels((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(row.budget_type_id!)) {
        newSet.delete(row.budget_type_id!);
      } else {
        newSet.add(row.budget_type_id!);
      }
      return newSet;
    });
  };

  const getStatusChangedLevels = () => {
    return rows.filter((row) => row.budget_type_id && changedLevels.has(row.budget_type_id));
  };

  const hasChanges = () => {
    return changedLevels.size > 0 || rows.some((row) => !row.budget_type_id && row.budget_type);
  };

  const handleSaveChanges = async () => {
    setFormLoading(true);
    try {
      const changedLevelsList = getStatusChangedLevels();

      const incrementedRows = [
        ...changedLevelsList.map((r) => ({
          ...r,
          level_number: r.level_number! + 1,
        })),
      ];

      const newLevels = rows.filter((row) => !row.budget_type_id && row.budget_type);

      // Update existing levels status
      for (const level of incrementedRows) {
        await updateLevel(level, !level.status);
      }

      // Add new levels
      for (const level of newLevels) {
        await handleAddSublevel(level);
      }

      notify("Изменения сохранены", "success");
      setChangedLevels(new Set());
      setIsEditMode(false);
      getLevelsData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при сохранении изменений");
    } finally {
      setFormLoading(false);
    }
  };

  useEffect(() => {
    if (levelId && levelItem) {
      setFormRecords({
        budget_type: levelItem.budget_type!,
        forecast_min_level: levelItem.forecast_min_level!,
      });

      const rows = levels
        .filter((lvl) => lvl.parent_budget_type_id === levelItem.budget_type_id)
        .map((r) => ({
          ...r,
          level_number: r.level_number! - 1,
        }));
      setRows(rows);

      const columns = rows.map((r) => r.level_number).filter((value, index, array) => array.indexOf(value) === index);
      setColumns(columns);

      setInitialLevels(rows);
    }
  }, [levelId, levelItem]);

  return (
    <>
      <ModalComponent
        header={levelId ? "Детали уровня" : "Новый уровень"}
        body={newLevelBody}
        footer={renderFooter()}
        disableClose={isFormLoading}
        shouldModalClose={isFormSubmitted}
      />
      <DangerModal
        modal_id="modal-deactivate-level"
        dangerDescription={deactivateLevelDanger}
        cancel={openLevelModal}
        confirm={handleLevelDelete}
      />
    </>
  );
}
